import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ModalMetadataEntryType} from 'analytics/AnalyticsTypes';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {TargetTaskIdAndStorables} from 'inventory/check-in-check-out/BaseCheckOutFromProcessTableModal';
import {getRestrictedSampleUsersDisplayText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {TargetStorableNoIcon} from 'notebook/structured-tables/action-functions/inventoryShortcutsActionFunction';
import {PartySummary} from 'parties/PartyConstants';
import {
  ProcessTasksCreatorsQuery,
  ProcessTasksCreatorsQueryVariables,
} from 'processes/process-task-details/graphql/__generated/ProcessTasksCreatorsQuery';
import {PROCESS_TASK_CREATORS_QUERY} from 'processes/process-task-details/graphql/ProcessTasksCreators';
import BulkUpdateRestrictedSampleUsersModal, {
  BulkUpdateRestrictedSampleUsersModalProps,
} from 'samples/toolbar/BulkUpdateRestrictedSampleUsersModal';

type ProcessTaskGQL = ExtractByTypename<ProcessTasksCreatorsQuery['processTasks'][number], 'ProcessTaskNode'>;

export interface BulkUpdateRestrictedSampleUsersFromProcessTableModalProps extends Omit<
  BulkUpdateRestrictedSampleUsersModalProps,
  'alternateParties'
> {
  targetTaskIdsAndStorables: Array<TargetTaskIdAndStorables>;
}

export default function BulkUpdateRestrictedSampleUsersFromProcessTableModal(
  props: BulkUpdateRestrictedSampleUsersFromProcessTableModalProps
): JSX.Element | null {
  const result = useBenchlingQuery<ProcessTasksCreatorsQuery, ProcessTasksCreatorsQueryVariables>(
    PROCESS_TASK_CREATORS_QUERY,
    {
      variables: {processTaskIds: props.targetTaskIdsAndStorables.map(({taskId}) => taskId)},
    }
  );

  switch (result.type) {
    case QueryResultType.SUCCESS: {
      const tasks = accessibleObjs(result.data.processTasks);
      const taskCreatorIdByTaskId: Map<string, PartySummary> = new Map();
      for (const task of tasks) {
        // A task can have multiple root tasks, but the auto-populated "task creator" needs a single creator.
        // Fall back to the first root task's creator as a heuristic (there's no unambiguous choice when a
        // task has more than one root), and to the task's own creator when it has no root task.
        const creator = task.rootTasks[0]?.creator ?? task.creator;
        if (creator.__typename === 'UserNode') {
          taskCreatorIdByTaskId.set(task.id, {...creator, type: ModelTypeConstants.USER});
        }
      }

      const partyIdsByItemId: {[itemId: string]: Set<string>} = objectFromEntries(
        props.items.map(({id}) => [id, new Set([])])
      );
      const taskById: {[taskId: string]: ProcessTaskGQL} = objectFromEntries(
        tasks.map((task) => [task.id, task])
      );
      const tasksByItemId: {[itemId: string]: Set<ProcessTaskGQL>} = objectFromEntries(
        props.items.map(({id}) => [id, new Set([])])
      );
      for (const {taskId, targetStorables} of props.targetTaskIdsAndStorables) {
        const task = taskById[taskId];
        const taskCreator = taskCreatorIdByTaskId.get(taskId);
        for (const item of targetStorables) {
          tasksByItemId[item.id].add(task);
          if (taskCreator) {
            partyIdsByItemId[item.id].add(taskCreator.id);
          }
        }
      }

      const itemIdsWithConflictingTaskCreators = Object.keys(partyIdsByItemId).filter(
        (itemId) => partyIdsByItemId[itemId].size > 1
      );
      let warning: React.ReactNode = null;
      if (itemIdsWithConflictingTaskCreators.length > 0) {
        const itemById: {[id: string]: TargetStorableNoIcon} = objectFromEntries(
          props.targetTaskIdsAndStorables.flatMap((foo) => foo.targetStorables).map((item) => [item.id, item])
        );
        const conflictMessages = itemIdsWithConflictingTaskCreators.map(
          (itemId) =>
            `${itemById[itemId].name}: ${[...tasksByItemId[itemId]].map(({name}) => name).join(', ')}`
        );
        warning = (
          <>
            <span>
              {`You can't update ${getRestrictedSampleUsersDisplayText()} using task creators because the following items appear in multiple tasks and have conflicting task creators:`}
            </span>
            <ul className={css(styles.conflictMessagesList)}>
              {conflictMessages.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          </>
        );
      }

      return (
        <BulkUpdateRestrictedSampleUsersModal
          {...props}
          alternateParties={{
            label: 'Task creator',
            partyIdsByItemId,
            disabled: warning != null,
            analyticsMetadataEntryType: ModalMetadataEntryType.AUTO_POPULATED_FROM_PROCESS_TASK,
          }}
          warning={warning}
        />
      );
    }
    default:
      return null;
  }
}

const styles = StyleSheet.create({
  conflictMessagesList: {
    paddingInlineStart: '16px',
    margin: '3px 0 0',
  },
});
